///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { Observable, Observer, Subscription } from 'rxjs';

import { WindowRef } from './window.service';

import * as SockJS from 'sockjs-client';
import * as Stomp from 'webstomp-client';
import { ActivityDTO } from './activity-dto.model';

@Injectable({
    providedIn: 'root'
})
export class SocketProjectService {
    public isConnected: boolean;
    private sockJS: SockJS;
    private stompClient = null;
    private subscriber = null;
    private connection: Promise<any>;
    private connectedPromise: any;
    private listener: Observable<ActivityDTO>;
    private listenerObserver: Observer<any>;
    private subscription: Subscription;

    constructor(
        private readonly $window: WindowRef,
        private readonly ngZone: NgZone
    ) {
        this.connection = this.createConnection();
    }

    connect() {
        if (this.sockJS) {
            this.disconnect();
        }

        if (this.connectedPromise === null) {
            this.connection = this.createConnection();
        }
        // building absolute path so that websocket doesn't fail when deploying with a context path
        const loc = this.$window.nativeWindow.location;
        const matches = /(?:^|; )XSRF-TOKEN=([^;]*)/.exec(document.cookie);
        const token = matches ? decodeURIComponent(matches[1]) : undefined;

        if (!token) {
            this.isConnected = false;
            return;
        }

        const url = `//${loc.host}${loc.pathname}websocket/project?_csrf=${token}`;
        this.createListener();

        const headers = {};
        headers['X-XSRF-TOKEN'] = token;

        this.sockJS = new SockJS(url);
        this.stompClient = Stomp.over(this.sockJS);
        this.stompClient.debug = () => {
            // disable debug info
        };

        this.stompClient.connect(
            headers,
            () => {
                this.connectedPromise('success');
                this.connectedPromise = null;
                this.isConnected = true;
            },
            (error) => {
                console.log(error);
                this.isConnected = false;
                this.disconnect();
            }
        );
    }

    disconnect() {
        if (this.listenerObserver) {
            this.listenerObserver.complete();
        }

        if (this.stompClient !== null) {
            this.stompClient.disconnect();
            this.stompClient = null;
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        if (this.sockJS) {
            this.sockJS.close();
            this.sockJS = null;
        }

        this.isConnected = false;
    }

    receive() {
        return this.listener;
    }

    sendActivity(projectId: number, activityDto: ActivityDTO) {
        if (this.stompClient !== null && this.stompClient.connected) {
            this.stompClient.send(
                '/topic/activity/' + projectId, // destination
                JSON.stringify(activityDto), // body
                {} // header
            );
        }
    }

    subscribe(projectId: number): Promise<void> {
        return new Promise((resolve) => {
            this.ngZone.runOutsideAngular(() => {
                this.connection.then(() => {
                    this.subscriber = this.stompClient.subscribe('/topic/tracker/' + projectId, (data) => {
                        this.ngZone.run(() => {
                            this.listenerObserver.next(JSON.parse(data.body));
                        });
                    });
                    resolve();
                });
            });
        });
    }

    unsubscribe() {
        if (this.subscriber !== null) {
            this.subscriber.unsubscribe();
            this.subscriber = null;
        }
    }

    private createListener(): void {
        this.ngZone.runOutsideAngular(() => {
            this.listener = new Observable((observer) => {
                this.ngZone.run(() => {
                    this.listenerObserver = observer;
                });
            });
        });
    }

    private createConnection(): Promise<any> {
        return new Promise((resolve, _reject) => (this.connectedPromise = resolve));
    }
}
